/**
 * Esitysmuoto käyttäjän kielellä.
 *
 * Kaksi eri tarkoitusta, jotka on pidettävä erillään:
 *
 * - `format*(arvo, kieli)` on ihmiselle näytölle: tuhaterottimet,
 *   euromerkki, kielen mukainen päiväysmuoto.
 * - `csv*` on tiedostoon: pelkkä desimaalipilkku, ei tuhaterottimia.
 *   Tuhaterotin rikkoisi CSV-solun luvun myös puolipisteerottimella.
 *   CSV-murre valitaan viennissä erikseen eikä se seuraa käyttöliittymän
 *   kieltä: tiedosto avataan usein eri koneella kuin se tehtiin.
 *
 * Muotoilu tehdään `Intl`-rajapinnalla eikä käsin kootuilla nimilistoilla,
 * jotta uuden kielen lisääminen on `LOCALES`-rivi eikä käännöstaulukko
 * kuukausille ja viikonpäiville. Suomen osalta lopputulos on merkki
 * merkiltä sama kuin ennen: `1 284,50 €`, `17.8.2026`, `elokuu`, `ma`.
 */
import { APP_TIME_ZONE } from './constants.js';
import { DEFAULT_CURRENCY } from './currency.js';
import { LOCALES } from './i18n.js';
import { parseIsoDate } from './time.js';
/** Yksiköt, joita `Intl` ei tunne. Tunti on `h` molemmilla kielillä. */
const YKSIKOT = {
    fi: { hours: 'h', days: 'htp' },
    en: { hours: 'h', days: 'pd' },
};
/**
 * Sitkeä välilyönti luvun ja yksikön väliin: `4,0 h` ei saa katketa
 * riville kahtia. Sama merkki, jonka `Intl` asettaa itse euromerkin ja
 * tuhansien eteen. Kirjoitettu escape-merkkinä, jotta se erottuu
 * tavallisesta välilyönnistä myös silmämääräisesti.
 */
const NBSP = '\u00A0';
/**
 * Muotoilijat ovat kalliita rakentaa ja halpoja pitää tallessa. Avaimena
 * lokaali ja asetukset, koska sama sovellus voi näyttää samalla
 * renderöinnillä sekä euroja että dollareita.
 */
const valimuisti = new Map();
function numeroMuotoilija(kieli, asetukset) {
    const avain = `n:${kieli}:${JSON.stringify(asetukset)}`;
    const valmis = valimuisti.get(avain);
    if (valmis)
        return valmis;
    const uusi = new Intl.NumberFormat(LOCALES[kieli], asetukset);
    valimuisti.set(avain, uusi);
    return uusi;
}
function nimiMuotoilija(kieli) {
    const avain = `c:${kieli}`;
    const valmis = valimuisti.get(avain);
    if (valmis)
        return valmis;
    const uusi = new Intl.DisplayNames([LOCALES[kieli]], { type: 'currency' });
    valimuisti.set(avain, uusi);
    return uusi;
}
function paivaMuotoilija(kieli, asetukset) {
    const avain = `d:${kieli}:${JSON.stringify(asetukset)}`;
    const valmis = valimuisti.get(avain);
    if (valmis)
        return valmis;
    // Aikavyöhyke on aina UTC: päivä tulee sisään merkkijonona ilman
    // kellonaikaa, eikä sitä saa siirtää vyöhykkeen yli toiseksi päiväksi.
    const uusi = new Intl.DateTimeFormat(LOCALES[kieli], { timeZone: 'UTC', ...asetukset });
    valimuisti.set(avain, uusi);
    return uusi;
}
/** ISO-päivä keskipäivän UTC-hetkeksi — vain muotoilijan syötteeksi. */
function utcPaiva(year, month, day) {
    return new Date(Date.UTC(year, month - 1, day));
}
// --- Luvut -----------------------------------------------------------------
/**
 * `"34040.00"` → `"34 040,00 €"` (fi) / `"€34,040.00"` (en)
 *
 * Sentit näytetään myös niissä valuutoissa, joissa ISO 4217 ei tunne
 * senttiä (jeni, forintti). Arvot on laskettu ja tallennettu kahdella
 * desimaalilla, ja `Intl`in oma pyöristys näyttäisi rivit ja loppusumman
 * eri tarkkuudella kuin kanta ne tietää — lasku ei täsmäisi raporttiin.
 */
export function formatMoney(value, kieli, currency = DEFAULT_CURRENCY) {
    return numeroMuotoilija(kieli, {
        style: 'currency',
        currency,
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    }).format(Number(value));
}
/**
 * Valuutan merkki sellaisena kuin se luvun vieressä näkyy: `EUR` → `€`,
 * `SEK` → `kr`, `USD` → `$`. Nimikkeisiin, joissa luku ei ole mukana
 * (”Tuntihinta €/h”) — niissä `formatMoney` ei auta, mutta pelkkä
 * koodikaan ei ole se, mitä lomakkeessa halutaan lukea.
 *
 * Merkki riippuu kielestä: brittienglannissa dollari on `US$`, suomessa
 * `$`. Siksi kieli on parametri eikä symboli ole taulukossa.
 */
export function currencySymbol(currency, kieli) {
    const osat = numeroMuotoilija(kieli, {
        style: 'currency',
        currency,
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
    }).formatToParts(0);
    return osat.find((osa) => osa.type === 'currency')?.value ?? currency;
}
/**
 * Valuutan nimi käyttäjän kielellä: `SEK` → `Ruotsin kruunu` /
 * `Swedish Krona`. Valintalistaan, jossa pelkkä koodi jättäisi arvaamisen
 * varaan. `Intl` ei tunne nimeä kaikilla kielillä — silloin koodi kelpaa
 * sellaisenaan.
 */
export function currencyName(currency, kieli) {
    return nimiMuotoilija(kieli).of(currency) ?? currency;
}
/** `"34040.00"` → `"34 040,00"` (ilman valuuttaa, esim. taulukkosarakkeeseen) */
export function formatDecimal(value, kieli) {
    return numeroMuotoilija(kieli, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    }).format(Number(value));
}
/** `240` minuuttia → `"4,0 h"` */
export function formatHours(minutes, kieli, withUnit = true) {
    const text = numeroMuotoilija(kieli, {
        minimumFractionDigits: 1,
        maximumFractionDigits: 1,
    }).format(minutes / 60);
    return withUnit ? `${text}${NBSP}${YKSIKOT[kieli].hours}` : text;
}
/** `"45.00"` → `"45 htp"` (fi) / `"45 pd"` (en) */
export function formatDays(days, kieli, withUnit = true) {
    const text = numeroMuotoilija(kieli, {
        minimumFractionDigits: 1,
        maximumFractionDigits: 2,
    }).format(Number(days));
    return withUnit ? `${text}${NBSP}${YKSIKOT[kieli].days}` : text;
}
export function formatPercent(share, kieli, decimals = 1) {
    if (share == null)
        return '–';
    return numeroMuotoilija(kieli, {
        style: 'percent',
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals,
    }).format(share);
}
/**
 * Tiedoston koko: `12093` → `11,8 kt` / `11.8 kB`.
 *
 * Kilotavu on 1024 tavua eikä 1000, koska luku esitetään tiedoston koosta
 * ja se on se luku, jonka käyttöjärjestelmän tiedostoselain näyttää.
 * Megatavuun asti riittää: tunnus on rajattu puoleen megatavuun.
 */
export function formatBytes(bytes, kieli) {
    const yksikko = kieli === 'fi' ? { kilo: 'kt', mega: 'Mt' } : { kilo: 'kB', mega: 'MB' };
    const megat = bytes / (1024 * 1024);
    const arvo = megat >= 1 ? megat : bytes / 1024;
    const teksti = numeroMuotoilija(kieli, {
        minimumFractionDigits: 0,
        maximumFractionDigits: 1,
    }).format(arvo);
    return `${teksti}${NBSP}${megat >= 1 ? yksikko.mega : yksikko.kilo}`;
}
// --- Päiväykset ------------------------------------------------------------
/** `"2026-08-17"` → `"17.8.2026"` (fi) / `"17/08/2026"` (en) */
export function formatDate(date, kieli) {
    const { year, month, day } = parseIsoDate(date);
    return paivaMuotoilija(kieli, {
        day: 'numeric',
        month: 'numeric',
        year: 'numeric',
    }).format(utcPaiva(year, month, day));
}
/** `"2026-08-17"` → `"17.8."` — kalenterin ja raportin tiiviit rivit */
export function formatDayMonth(date, kieli) {
    const { year, month, day } = parseIsoDate(date);
    return paivaMuotoilija(kieli, { day: 'numeric', month: 'numeric' }).format(utcPaiva(year, month, day));
}
/**
 * Aikaleima ihmiselle: `"2026-08-17T06:05:00.000Z"` → `"17.8.2026 klo 9.05"`.
 *
 * Toisin kuin kirjauspäivä, aikaleima on todellinen hetki, joten se
 * näytetään sovelluksen aikavyöhykkeellä eikä UTC:nä — muuten kesäaikaan
 * kirjattu rivi näyttäisi kolme tuntia väärältä.
 */
export function formatDateTime(timestamp, kieli) {
    const hetki = new Date(timestamp);
    if (Number.isNaN(hetki.getTime()))
        return '–';
    const avain = `dt:${kieli}`;
    const valmis = valimuisti.get(avain);
    const muotoilija = valmis ??
        new Intl.DateTimeFormat(LOCALES[kieli], {
            timeZone: APP_TIME_ZONE,
            dateStyle: 'short',
            timeStyle: 'short',
        });
    if (!valmis)
        valimuisti.set(avain, muotoilija);
    return muotoilija.format(hetki);
}
/**
 * Aikaleima sekunnin tarkkuudella: `"24.9.2026 klo 14.02.17"`.
 *
 * Salaisuuden lukuhetki on se tieto, jolla jakaja erottaa asiakkaan
 * lukemisen kaappaajan lukemisesta, joten minuutti ei riitä.
 */
export function formatDateTimeSeconds(timestamp, kieli) {
    const hetki = new Date(timestamp);
    if (Number.isNaN(hetki.getTime()))
        return '–';
    const avain = `dts:${kieli}`;
    const valmis = valimuisti.get(avain);
    const muotoilija = valmis ??
        new Intl.DateTimeFormat(LOCALES[kieli], {
            timeZone: APP_TIME_ZONE,
            dateStyle: 'short',
            timeStyle: 'medium',
        });
    if (!valmis)
        valimuisti.set(avain, muotoilija);
    return muotoilija.format(hetki);
}
/** `8` → `"elokuu"` / `"August"` */
export function monthName(month, kieli) {
    return paivaMuotoilija(kieli, { month: 'long' }).format(utcPaiva(2000, month, 1));
}
/** `2026, 8` → `"Elokuu 2026"` / `"August 2026"` */
export function formatMonthTitle(year, month, kieli) {
    const name = monthName(month, kieli);
    return `${name.charAt(0).toLocaleUpperCase(LOCALES[kieli])}${name.slice(1)} ${year}`;
}
/** ISO-viikonpäivä 1–7 → `"maanantai"` / `"Monday"` */
export function weekdayName(isoWeekday, kieli) {
    // 2024-01-01 oli maanantai, joten ISO-numero on suoraan kuukaudenpäivä.
    return paivaMuotoilija(kieli, { weekday: 'long' }).format(utcPaiva(2024, 1, isoWeekday));
}
/** ISO-viikonpäivä 1–7 → `"ma"` / `"Mon"` */
export function weekdayAbbr(isoWeekday, kieli) {
    return paivaMuotoilija(kieli, { weekday: 'short' }).format(utcPaiva(2024, 1, isoWeekday));
}
// --- CSV -------------------------------------------------------------------
/** `"115.00"` → `"115,00"`. Ei tuhaterottimia — ne rikkoisivat solun. */
export function csvDecimal(value, dialect) {
    if (value == null)
        return '';
    return dialect === 'excel-fi' ? value.replace('.', ',') : value;
}
export function csvBoolean(value, dialect) {
    if (dialect === 'standard')
        return value ? 'true' : 'false';
    return value ? 'kyllä' : 'ei';
}
//# sourceMappingURL=format.js.map